<template>
  <div class="control is-horizontal footer-logos-control">
    <div class="control-label">
      <label-name labelKey="SITE-PWA-FOOTER_LOGOS"></label-name>
    </div>

    <div class="control footer-logos-container">
      <!-- LOGO BLOCKS LIST -->
      <div v-if="items.length > 0" class="footer-logos-list">
        <div
          v-for="(item, index) in items"
          :key="'logo-' + index"
          class="footer-logo-card"
        >
          <!-- 1. INDEX & REORDER HANDLE -->
          <div class="logo-card-handle">
            <span class="tag is-rounded is-light logo-index">#{{ index + 1 }}</span>
            <div class="reorder-buttons">
              <button
                type="button"
                class="button is-small is-light btn-reorder"
                :disabled="index === 0"
                @click="moveUp(index)"
                title="Nach oben verschieben"
              >
                <span class="material-symbols-outlined">keyboard_arrow_up</span>
              </button>
              <button
                type="button"
                class="button is-small is-light btn-reorder"
                :disabled="index === items.length - 1"
                @click="moveDown(index)"
                title="Nach unten verschieben"
              >
                <span class="material-symbols-outlined">keyboard_arrow_down</span>
              </button>
            </div>
          </div>

          <!-- 2. IMAGE PREVIEW THUMBNAIL -->
          <div class="logo-preview-box">
            <img
              v-if="item.img && !imgErrors[index]"
              :src="item.img"
              :alt="item.title || 'Footer Logo'"
              class="logo-thumbnail"
              @error="handleImgError(index)"
              @load="handleImgLoad(index)"
            />
            <div v-else class="logo-placeholder">
              <span class="material-symbols-outlined">image</span>
              <span class="placeholder-text">{{ item.img ? 'Fehler' : 'Kein Bild' }}</span>
            </div>
          </div>

          <!-- 3. INPUT FIELDS (STACKED VERTICALLY) -->
          <div class="logo-fields-stacked">
            <!-- TITEL / BEZEICHNUNG -->
            <div class="logo-field-row">
              <span class="logo-field-label">Titel / Name</span>
              <div class="logo-field-input-wrap">
                <input
                  type="text"
                  class="input is-small"
                  v-model="item.title"
                  placeholder="z.B. Bewerten Sie uns auf Google!"
                  @input="emitUpdate"
                />
              </div>
            </div>

            <!-- BILD-URL -->
            <div class="logo-field-row">
              <span class="logo-field-label">Bild-URL (img)</span>
              <div class="logo-field-input-wrap">
                <input
                  type="text"
                  class="input is-small"
                  v-model="item.img"
                  placeholder="https://.../logo.png"
                  @input="onImgUrlChanged(index)"
                />
              </div>
            </div>

            <!-- VERLINKUNG (URL) -->
            <div class="logo-field-row">
              <span class="logo-field-label">Verlinkung (url)</span>
              <div class="logo-field-input-wrap">
                <div class="url-input-container">
                  <input
                    type="text"
                    class="input is-small"
                    v-model="item.url"
                    placeholder="https://..."
                    @input="emitUpdate"
                  />
                  <a
                    v-if="isValidUrl(item.url)"
                    :href="item.url"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="button is-small is-light btn-open-link"
                    title="Link in neuem Tab öffnen"
                  >
                    <span class="material-symbols-outlined">open_in_new</span>
                  </a>
                </div>
              </div>
            </div>
          </div>

          <!-- 4. ACTION BUTTONS (DUPLICATE & DELETE) -->
          <div class="logo-card-actions">
            <button
              type="button"
              class="button is-small is-light is-icon-only btn-card-action"
              title="Logo duplizieren"
              @click="duplicateLogo(index)"
            >
              <span class="material-symbols-outlined">content_copy</span>
            </button>
            <button
              type="button"
              class="button is-small is-danger is-outline is-icon-only btn-card-action btn-delete"
              title="Logo entfernen"
              @click="removeLogo(index)"
            >
              <span class="material-symbols-outlined">delete</span>
            </button>
          </div>
        </div>
      </div>

      <!-- EMPTY STATE -->
      <div v-else class="empty-logos-state">
        <span class="material-symbols-outlined empty-state-icon">featured_play_list</span>
        <p class="mb-2">Noch keine Footer-Logos hinterlegt.</p>
        <button
          type="button"
          class="button is-blue is-outline is-small"
          @click="addLogo"
        >
          <span class="material-symbols-outlined">add</span>
          Erstes Footer-Logo hinzufügen
        </button>
      </div>

      <!-- ADD BUTTON & TOOLBAR -->
      <div v-if="items.length > 0" class="logos-toolbar">
        <button
          type="button"
          class="button is-blue is-outline is-small btn-add-logo"
          @click="addLogo"
        >
          <span class="material-symbols-outlined">add</span>
          Logo hinzufügen
        </button>

        <span class="logos-counter tag is-light is-small ml-2">
          {{ items.length }} Logo(s) hinterlegt
        </span>
      </div>

      <!-- INFO HELP TEXT -->
      <span class="help is-info-text">
        Logos und Partner-Verlinkungen im Footer der PWA (z.B. Apothekerkammer, Gütesiegel, Zertifikate).
      </span>

      <!-- FOOTER: RAW JSON PANEL -->
      <pharmacy-raw-json-panel
        :json-data="serializeData()"
        title="JSON-Rohdaten (Footer-Logos)"
        :rows="8"
        placeholder="JSON hier einfügen... (z.B. [{ img: '...', title: '...', url: '...' }])"
        @apply="onApplyRawJson"
      />
    </div>
  </div>
</template>

<script>
import PharmacyRawJsonPanel from './incl-pharmacy-raw-json-panel.vue';

export default {
  name: 'PharmacyFooterLogos',

  components: {
    PharmacyRawJsonPanel
  },

  props: {
    // Array of objects [{ img: "", title: "", url: "" }] or JSON string
    value: {
      type: [Array, String],
      default: () => []
    }
  },

  data() {
    return {
      items: this.parseValue(this.value),
      imgErrors: {},
      isInternalUpdate: false
    };
  },

  watch: {
    value: {
      deep: true,
      handler(newVal) {
        if (this.isInternalUpdate) {
          this.isInternalUpdate = false;
          return;
        }
        this.items = this.parseValue(newVal);
        this.imgErrors = {};
      }
    }
  },

  methods: {
    parseValue(val) {
      let data = val;
      if (typeof val === 'string' && val.trim() !== '') {
        try {
          data = JSON.parse(val);
        } catch (e) {
          data = [];
        }
      }
      if (!Array.isArray(data)) {
        data = [];
      }

      return data.map(item => ({
        img: (item && item.img) || '',
        title: (item && item.title) || '',
        url: (item && item.url) || ''
      }));
    },

    serializeData() {
      return this.items.map(item => ({
        img: (item.img || '').trim(),
        title: (item.title || '').trim(),
        url: (item.url || '').trim()
      }));
    },

    emitUpdate() {
      const payload = this.serializeData();
      this.isInternalUpdate = true;
      this.$emit('input', payload);
      this.$emit('change', payload);

      if (this.showRawJson) {
        this.rawJsonInput = JSON.stringify(payload, null, 2);
      }
    },

    addLogo() {
      this.items.push({
        img: '',
        title: '',
        url: ''
      });
      this.emitUpdate();
    },

    removeLogo(index) {
      this.items.splice(index, 1);
      this.$delete(this.imgErrors, index);
      this.emitUpdate();
    },

    duplicateLogo(index) {
      const source = this.items[index];
      const clone = {
        img: source.img,
        title: source.title ? source.title + ' (Kopie)' : '',
        url: source.url
      };
      this.items.splice(index + 1, 0, clone);
      this.emitUpdate();
    },

    moveUp(index) {
      if (index <= 0) return;
      const item = this.items.splice(index, 1)[0];
      this.items.splice(index - 1, 0, item);
      this.imgErrors = {};
      this.emitUpdate();
    },

    moveDown(index) {
      if (index >= this.items.length - 1) return;
      const item = this.items.splice(index, 1)[0];
      this.items.splice(index + 1, 0, item);
      this.imgErrors = {};
      this.emitUpdate();
    },

    handleImgError(index) {
      this.$set(this.imgErrors, index, true);
    },

    handleImgLoad(index) {
      this.$set(this.imgErrors, index, false);
    },

    onImgUrlChanged(index) {
      this.$set(this.imgErrors, index, false);
      this.emitUpdate();
    },

    isValidUrl(url) {
      if (!url || typeof url !== 'string') return false;
      return /^https?:\/\/[^\s/$.?#].[^\s]*/i.test(url.trim());
    },

    onApplyRawJson(parsed, done) {
      if (!Array.isArray(parsed)) {
        return done('Das JSON muss ein Array von Objekten sein (z.B. [{ img: "...", title: "...", url: "..." }]).');
      }
      this.items = this.parseValue(parsed);
      this.imgErrors = {};
      this.emitUpdate();
      done();
    }
  }
};
</script>

<style lang="scss" scoped>
.footer-logos-control {
  margin-top: 10px;
  margin-bottom: 20px;
  align-items: flex-start;

  > .control-label {
    padding-top: 6px;
  }
}

.footer-logos-container {
  flex: 1;
  max-width: 950px;
  width: 100%;
}

/* LIST OF LOGO CARDS */
.footer-logos-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.footer-logo-card {
  display: flex;
  align-items: center;
  background-color: #fff;
  border: 1px solid #e1e6eb;
  border-radius: 6px;
  padding: 12px 14px;
  gap: 14px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &:hover {
    border-color: #cbd5e1;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.06);
  }
}

/* HANDLE & NUMBERING */
.logo-card-handle {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  min-width: 36px;

  .logo-index {
    font-size: 11px;
    font-weight: 700;
    color: #555;
    background-color: #f0f3f6;
    padding: 0 6px;
    height: 20px;
    line-height: 20px;
  }

  .reorder-buttons {
    display: flex;
    flex-direction: column;
    gap: 2px;

    .btn-reorder {
      width: 22px !important;
      height: 20px !important;
      min-width: 22px !important;
      padding: 0 !important;
      border: none !important;
      background: transparent !important;
      color: #7a7a7a !important;
      cursor: pointer;
      display: inline-flex !important;
      align-items: center !important;
      justify-content: center !important;
      line-height: 1 !important;

      .material-symbols-outlined {
        margin: 0 !important;
        padding: 0 !important;
        font-size: 18px !important;
        line-height: 1 !important;
        display: inline-flex !important;
        align-items: center !important;
        justify-content: center !important;
      }

      &:hover:not(:disabled) {
        color: #2780e3 !important;
        background-color: #f0f4f8 !important;
        border-radius: 3px !important;
      }

      &:disabled {
        opacity: 0.3 !important;
        cursor: not-allowed;
      }
    }
  }
}

/* IMAGE PREVIEW THUMBNAIL */
.logo-preview-box {
  width: 90px;
  height: 90px;
  min-width: 90px;
  background-color: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  position: relative;

  .logo-thumbnail {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
    padding: 6px;
  }

  .logo-placeholder {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    color: #94a3b8;

    .material-symbols-outlined {
      font-size: 24px;
    }

    .placeholder-text {
      font-size: 10px;
      line-height: 1;
      margin-top: 4px;
    }
  }
}

/* INPUT FIELDS (STACKED VERTICALLY) */
.logo-fields-stacked {
  display: flex;
  flex-direction: column;
  flex: 1;
  gap: 6px;
  min-width: 0;

  .logo-field-row {
    display: flex;
    align-items: center;
    gap: 10px;

    .logo-field-label {
      width: 125px;
      min-width: 125px;
      font-size: 11px;
      font-weight: 600;
      color: #64748b;
      text-transform: uppercase;
      letter-spacing: 0.3px;
    }

    .logo-field-input-wrap {
      flex: 1;
      min-width: 0;

      .control {
        margin-bottom: 0 !important;
        width: 100%;
        max-width: 100% !important;
      }

      .input.is-small {
        height: 32px;
        font-size: 13px;
        border-radius: 3px;
        border-color: #dbdbdb;
        padding-left: 10px;
        padding-right: 10px;
        width: 100%;

        &:focus {
          border-color: #45a3d2;
        }
      }

      .url-input-container {
        display: flex !important;
        align-items: center !important;
        width: 100% !important;
        gap: 6px;

        .input {
          flex: 1 1 auto !important;
          width: 100% !important;
        }

        .btn-open-link {
          flex: 0 0 32px !important;
          height: 32px !important;
          width: 32px !important;
          min-width: 32px !important;
          padding: 0 !important;
          display: inline-flex !important;
          align-items: center !important;
          justify-content: center !important;
          border-radius: 4px !important;
          color: #64748b;
          line-height: 1 !important;

          .material-symbols-outlined {
            margin: 0 !important;
            padding: 0 !important;
            font-size: 16px !important;
            line-height: 1 !important;
            display: inline-flex !important;
            align-items: center !important;
            justify-content: center !important;
          }

          &:hover {
            color: #2780e3 !important;
            background-color: #f1f5f9 !important;
          }
        }
      }
    }
  }
}

/* ACTIONS: DUPLICATE & DELETE (CENTERED ICONS) */
.logo-card-actions {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  min-width: 32px;

  .button.is-icon-only,
  .btn-card-action {
    height: 28px !important;
    width: 28px !important;
    min-width: 28px !important;
    border-radius: 4px !important;
    padding: 0 !important;
    display: inline-flex !important;
    align-items: center !important;
    justify-content: center !important;
    margin: 0 !important;
    line-height: 1 !important;

    .material-symbols-outlined,
    .icon,
    span {
      margin: 0 !important;
      margin-right: 0 !important;
      margin-left: 0 !important;
      padding: 0 !important;
      font-size: 15px !important;
      line-height: 1 !important;
      display: inline-flex !important;
      align-items: center !important;
      justify-content: center !important;
      text-align: center !important;
    }

    &.btn-delete {
      color: #fff !important;
      background-color: #e74c3c !important;
      border-color: #e74c3c !important;

      &:hover {
        background-color: #c0392b !important;
        border-color: #c0392b !important;
      }
    }
  }
}

/* EMPTY STATE */
.empty-logos-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background-color: #fafbfc;
  border: 1px dashed #cbd5e1;
  border-radius: 6px;
  color: #64748b;
  text-align: center;
  font-size: 13px;

  .empty-state-icon {
    font-size: 32px;
    color: #94a3b8;
    margin-bottom: 6px;
  }
}

/* TOOLBAR */
.logos-toolbar {
  display: flex;
  align-items: center;
  margin-top: 10px;

  .btn-add-logo {
    height: 28px;
    font-size: 12px;
    border-radius: 4px;

    .material-symbols-outlined {
      font-size: 16px;
      margin-right: 4px;
    }
  }

  .logos-counter {
    height: 24px;
    font-size: 11px;
    color: #64748b;
  }
}

.help.is-info-text {
  display: block;
  font-size: 11px;
  color: #7a7a7a;
  margin-top: 8px;
  margin-bottom: 0;
  line-height: 1.4;
}

</style>
